import { useLocation } from "@/utils/location-provider";

import { DEFAULT_LOCALE, LOCALES } from "./localization.constants";
import { useLocale } from "./use-locale";

export function isLocalizableHref(href: string) {
    // only paths starting with a single slash to prevent localizing URLs
    // starts with a slash, followed by the end of the string or anything that's not a slash, excludes /blog/* urls
    return /^(?!\/\/)\/(blog$|$|(?!blog\/).+)/.test(href);
}

export function isAlreadyLocalizedHref(href: string) {
    return (
        LOCALES.some((locale) => href.startsWith(`/${locale}/`)) ||
        LOCALES.some((locale) => href === `/${locale}`)
    );
}

export interface UseLocalizedHrefOptions {
    supportedLocales?: string[];
}

// Functions to check if known domain -  treat localhost as matching all domains for testing purposes
function isWacPage(hostname: string): boolean {
    if (!hostname) return true;
    const lower = hostname.toLowerCase();
    return (
        lower === "www.atlassian.com" ||
        lower.startsWith("wac.") ||
        lower === "localhost" ||
        lower === "127.0.0.1"
    );
}

function isIdPage(hostname: string): boolean {
    if (!hostname) return true;
    const lower = hostname.toLowerCase();

    return (
        lower === "localhost" ||
        lower === "127.0.0.1" ||
        (lower.endsWith(".atlassian.com") && lower.split(".")[0] === "id")
    );
}

function isJacPage(hostname: string): boolean {
    if (!hostname) return true;
    const lower = hostname.toLowerCase();

    return (
        lower === "localhost" ||
        lower === "127.0.0.1" ||
        (lower.endsWith(".atlassian.com") && lower.split(".")[0] === "jira")
    );
}

function getWacBaseUrl(hostname: string) {
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "http://localhost:9000";
    }
    if (hostname.includes("dev")) {
        return "https://wac.dev.internal.atlassian.com";
    }
    if (hostname.includes("stg")) {
        return "https://wac.stg.internal.atlassian.com";
    }
    return "https://www.atlassian.com";
}

/**
 * Hostname from the last useLocalizedHref (or useLocation) run in the tree.
 * Used by absoluteToRelativeHref so it can stay a plain function (no hooks).
 */
let currentHostname: string | undefined;

function getCurrentHostname(): string {
    if (currentHostname !== undefined) return currentHostname;
    if (typeof window !== "undefined") {
        return window.location?.hostname ?? "";
    }
    return "";
}

// Localized relative URL for WAC pages, full URL for non-WAC pages
export function useLocalizedHref(
    href: string,
    opts: UseLocalizedHrefOptions = {}
) {
    const { supportedLocales } = opts;
    const locale = useLocale();
    const localizedRelativePath = getLocalizedHref({
        locale,
        href,
        supportedLocales
    });

    const location = useLocation();
    currentHostname = location.hostname;
    const onWacPage = isWacPage(location.hostname);

    if (onWacPage) {
        return localizedRelativePath;
    }

    return relativeToAbsoluteHref(localizedRelativePath, location.hostname);
}

export interface GetLocaledHrefParams {
    locale: string;
    href: string;
    supportedLocales?: string[];
}

/**
 * Slug form (no leading slash), e.g. `blog/post-name` — do not locale-prefix
 * (legacy /blog/* behavior; matches {@link isLocalizableHref} for `/blog/...` paths).
 */
export function isBlogArticlePathSlug(slug: string): boolean {
    return /^blog\/.+/.test(slug.replace(/^\//, ""));
}

export function getLocalizedHref({
    locale,
    href,
    supportedLocales
}: GetLocaledHrefParams) {
    if (
        locale === DEFAULT_LOCALE ||
        isAlreadyLocalizedHref(href) ||
        !isLocalizableHref(href)
    ) {
        return href;
    }

    if (supportedLocales) {
        return supportedLocales.includes(locale) ? `/${locale}${href}` : href;
    }

    return `/${locale}${href}`;
}

/**
 * Path slug form (no leading slash) for rich-text HTML string replacements
 * (`href="/${slug}"`). Uses the same rules as {@link getLocalizedHref} (blog
 * paths, already-localized paths, default locale).
 */
export function getLocalizedHrefForPathSlug(
    slug: string,
    locale: string,
    supportedLocales?: string[]
): string {
    const slugNoLeadingSlash = slug.replace(/^\//, "");
    if (isBlogArticlePathSlug(slugNoLeadingSlash)) {
        return slugNoLeadingSlash;
    }
    const href = slug.startsWith("/") ? slug : `/${slug}`;
    const localized = getLocalizedHref({ locale, href, supportedLocales });
    return localized.replace(/^\//, "");
}

type AbsoluteToRelativeHrefRule = {
    readonly pattern: RegExp;
    readonly isApplicableHostname: (hostname: string) => boolean;
};

// Continue to add domains to rules as needed
const ABSOLUTE_TO_RELATIVE_HREF_RULES: readonly AbsoluteToRelativeHrefRule[] = [
    {
        pattern: /^https?:\/\/(www\.)?atlassian\.com/,
        isApplicableHostname: isWacPage
    },
    {
        pattern: /^https?:\/\/id\.atlassian\.com/,
        isApplicableHostname: isIdPage
    },
    {
        pattern: /^https?:\/\/jira\.atlassian\.com/,
        isApplicableHostname: isJacPage
    }
];

/**
 * Converts an absolute URL to a relative path when the current
 * page is a known domain, and the url and page domains match.
 */
export function absoluteToRelativeHref(href: string): string {
    const hostname = getCurrentHostname();

    for (const rule of ABSOLUTE_TO_RELATIVE_HREF_RULES) {
        if (!rule.isApplicableHostname(hostname)) {
            continue;
        }
        if (!rule.pattern.test(href)) {
            continue;
        }
        return href.replace(rule.pattern, "");
    }

    return href;
}

function relativeToAbsoluteHref(
    localizedHref: string,
    hostname: string
): string {
    if (
        localizedHref.startsWith("http://") ||
        localizedHref.startsWith("https://") // only pre-pend WAC base URL for relative paths
    ) {
        return localizedHref;
    }
    const baseUrl = getWacBaseUrl(hostname);
    return `${baseUrl}${localizedHref}`;
}

function getTrelloPath(trelloRoute: string) {
    if (trelloRoute === "/") {
        return "";
    }
    return trelloRoute;
}

export function useLocalizedHrefTrello(trelloRoute: string) {
    const trelloBaseUrl = "https://trello.com";
    const locale = useLocale();
    const trelloPath = getTrelloPath(trelloRoute);
    if (locale === DEFAULT_LOCALE) {
        return `${trelloBaseUrl}${trelloPath}`;
    }
    return `${trelloBaseUrl}/${locale}${trelloPath}`;
}
